// The hover language's one list (law_list_language v2, 2026-09-23).
//
// Everything the global tracker (components/HoverField.tsx) may light with
// `data-hl`. The same list is written out in globals.css as the `:where(…)`
// lists marked `/* HL_SELECTOR */` (the wash + ink uptake rule); hover.test.ts
// fails if the two drift. Add a class here AND there, or give a one-off
// element `data-hl-target` instead.
//
// Pure: no DOM at import time, so the list and the geometry are testable.

/** every class the tracker lights, in the order the CSS lists them */
export const HL_CLASSES = [
  "list-row",
  "feed-item",
  "task-row",
  "st-row__btn",
  "st-entry",
  "home-tile",
  "chip",
  "icon-circle",
  "btn-pill",
  "orbit-tile",
  "os-fullmenu__row",
  "art-tab",
  "rp-row",
  "ac-row",
  "yo-refbtn",
  "yo-day",
  "yo-cal__day",
  "yo-mirror-word",
  "yo-private-fact",
] as const;

/** the opt-in attribute for anything that is not one of the classes above */
export const HL_TARGET_ATTR = "data-hl-target";

/** the one selector the tracker hands to `closest()` */
export const HL_SELECTOR = [...HL_CLASSES.map((c) => `.${c}`), `[${HL_TARGET_ATTR}]`].join(", ");

/** set on the element under the pointer (and on each lit ancestor) */
export const HL_ATTR = "data-hl";
/** set on <html> once the tracker runs; the CSS falls back to :hover without it */
export const HL_JS_ATTR = "data-hl-js";
/** a proximity zone: `data-hl-near="28"` = within 28px above/below */
export const HL_NEAR_ATTR = "data-hl-near";
export const HL_NEAR_SELECTOR = `[${HL_NEAR_ATTR}]`;
/** set on a zone while the pointer is near it */
export const NEAR_ATTR = "data-near";
/** how far to the side a zone reaches, whatever its vertical reach */
export const NEAR_PAD_X = 12;
/** nested targets lit at once (a chip inside a row lights both, like :hover) */
export const HL_MAX_CHAIN = 4;

export interface RectLike {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** the zone's vertical reach from its attribute; junk reads as 0 (the rect itself) */
export function nearPad(attr: string | null | undefined): number {
  const n = Number(attr);
  return Number.isFinite(n) && n > 0 ? Math.min(n, 400) : 0;
}

/** is the point within the rect inflated by `padY` vertically and `padX` sideways? */
export function nearHit(r: RectLike, x: number, y: number, padY: number, padX: number = NEAR_PAD_X): boolean {
  if (r.right <= r.left || r.bottom <= r.top) return false; // hidden / collapsed
  return x >= r.left - padX && x <= r.right + padX && y >= r.top - padY && y <= r.bottom + padY;
}

/** the glow's anchor inside a target, whole pixels (fewer distinct style writes) */
export function glowPoint(r: RectLike, x: number, y: number): { mx: string; my: string } {
  return { mx: `${Math.round(x - r.left)}px`, my: `${Math.round(y - r.top)}px` };
}

/** transitions that move a thing under a still pointer → re-hit-test after them */
export function movesLayout(propertyName: string): boolean {
  return (
    propertyName === "transform" ||
    propertyName === "translate" ||
    propertyName === "left" ||
    propertyName === "top" ||
    propertyName === "right" ||
    propertyName === "bottom"
  );
}

/** how far a captured pointer travels before a press counts as a drag (px). A
 *  press that takes pointer capture (use-gesture's useDrag does, on every tile)
 *  keeps its hover lift; only a real drag drops it. */
export const DRAG_SLOP = 4;

/** has a captured pointer moved from where it was pressed far enough to be a drag? */
export function isDrag(dx: number, dy: number, slop: number = DRAG_SLOP): boolean {
  return dx * dx + dy * dy > slop * slop;
}
